import { Suspense, lazy } from "react";
import { Navigate, useLocation, useRoutes } from "react-router-dom";
// layouts
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
import DashboardLayout from "../layouts/dashboard";
// guards
import GuestGuard from "../guards/GuestGuard";
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from "../core/components/LoadingScreen";
//pages
import DashboardNavigator from "../pages/dashboard/DashboardNavigator";
import DashboardRouter from "../pages/dashboard/DashboardRouter";
import FileDetail from "../pages/files/FileDetail";
import UploadAudioFile from "../pages/files/UploadAudioFile";
import ApiKeys from "../pages/dashboard/ApiKeys";
import Statistic from "../pages/reports/statistic";
import Usage from "../pages/reports/usage";
import AccessAuditList from "../pages/access/AccessAudit";
import AccessAuditDetail from "../pages/access/AccessAuditDetail";
import AzureAuth from "../pages/dashboard/AzureAuth";
import { AuthGuard } from "../guards/AuthGuard";
// import Authorization from 'pages/admin/Authorization';
// ----------------------------------------------------------------------

const Loadable = (Component: React.ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes("/dashboard");

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: "fixed",
            }),
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: "login/oauth2/redirect",
      element: <AzureAuth />,
    },
    // Dashboard Routes
    {
      path: "dashboard",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/app" replace /> },
        { path: "app", element: <DashboardNavigator /> },
        { path: "api-keys", element: <ApiKeys /> },
        {
          path: "file",
          children: [
            { path: "upload-audio", element: <UploadAudioFile /> },
            { path: ":id", element: <FileDetail /> },
          ],
        },
        {
          path: "reports",
          children: [
            { path: "usage", element: <Usage /> },
            { path: "statistic", element: <Statistic /> },
          ],
        },
        {
          path: "access-audit",
          children: [
            { index: true, element: <AccessAuditList /> },
            { path: ":recordId", element: <AccessAuditDetail /> },
          ],
        },
      ],
    },
    // Admin Routes
    {
      path: "admin",
      children: [
        {
          path: "auth",
          children: [
            {
              path: "login",
              element: <GuestGuard>{/* <AdminLogin /> */}</GuestGuard>,
            },
          ],
        },
        {
          path: "dashboard",
          element: (
            <AuthGuard>
              <DashboardLayout />
            </AuthGuard>
          ),
          children: [
            { element: <Navigate to="/admin/dashboard/app" replace /> },
            // { path: 'app', element: <Authorization /> },
            {
              path: "panel",
              children: [
                {
                  path: "authorization",
                  // element: <Authorization />
                },
              ],
            },
          ],
        },
      ],
    },
    // Main Routes
    {
      path: "*",
      element: <LogoOnlyLayout />,
      children: [
        // { path: '500', element: <Page500 /> },
        { path: "404", element: <NotFound /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: "/",
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
      children: [
        {
          path: "user",
          children: [
            { element: <Navigate to="/user/profile" replace /> },
            {
              path: "profile",
              element: (
                <AuthGuard>
                  <ProfilePage />
                </AuthGuard>
              ),
            },
          ],
        },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// Authentication
const Login = Loadable(lazy(() => import("../pages/authentication/Login")));
// const AdminLogin = Loadable(lazy(() => import('../pages/admin/authentication/Login')));
// const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));
const ProfilePage = Loadable(
  lazy(() => import("../pages/profile/ProfilePage"))
);
